/**
 * ReferenceDataMobileHeader Component
 *
 * Shared header for mobile reference data pages (Brokers, Instruments).
 * Renders breadcrumbs (Settings > Portfolio) + Select dropdown acting as page title.
 *
 * Pattern matches Portfolio/ReferenceDataPage mobile layout and
 * Strategies/ReferenceDataPage mobile layout.
 */

// 1. External dependencies
import React from 'react';
import { useNavigate } from 'react-router-dom';

// 2. Workspace packages
import {
  Card,
  CardHeader,
  Breadcrumbs,
  BreadcrumbItem,
  Select,
  SelectItem,
} from '@enfomentis/ui';

type SectionKey = 'brokers' | 'instruments';

const SECTIONS: SectionKey[] = ['brokers', 'instruments'];

const SECTION_LABELS: Record<SectionKey, string> = {
  brokers: 'Brokers',
  instruments: 'Instruments',
};

const SECTION_ROUTES: Record<SectionKey, string> = {
  brokers: '/settings/portfolio/reference-data/brokers',
  instruments: '/settings/portfolio/reference-data/instruments',
};

interface ReferenceDataMobileHeaderProps {
  activeSection: SectionKey;
}

export const ReferenceDataMobileHeader: React.FC<ReferenceDataMobileHeaderProps> = ({
  activeSection,
}) => {
  const navigate = useNavigate();

  const handleSectionChange = (keys: 'all' | Set<React.Key>): void => {
    if (keys === 'all') return;
    const selected = Array.from(keys)[0];
    if (!selected || String(selected) === activeSection) return;
    const route = SECTION_ROUTES[String(selected) as SectionKey];
    if (route) navigate(route);
  };

  return (
    <Card radius="none" shadow="none">
      <CardHeader className="flex-col items-start pt-1 pb-0 border-b border-default-200">
        {/* Breadcrumb navigation — parent context only */}
        <div className="flex items-center pb-1">
          <Breadcrumbs variant="light" size="sm">
            <BreadcrumbItem
              onPress={() => navigate('/settings')}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={() => navigate('/settings/portfolio')}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Portfolio
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>

        {/* Section selector dropdown — acts as page title */}
        <Select
          aria-label="Select section"
          selectedKeys={[activeSection]}
          onSelectionChange={handleSectionChange}
          className="mt-1 w-full"
          classNames={{
            trigger: 'bg-transparent shadow-none data-[hover=true]:bg-default-50 px-0 min-h-unit-8',
            value: '!text-text-primary text-lg font-semibold',
          }}
          listboxProps={{
            itemClasses: {
              base: [
                'data-[hover=true]:!text-primary data-[hover=true]:!bg-transparent',
                'data-[selectable=true]:focus:!bg-transparent data-[selectable=true]:focus:!text-primary',
                'data-[selected=true]:!text-primary data-[selected=true]:!bg-transparent',
              ].join(' '),
            },
          }}
        >
          {SECTIONS.map((key) => (
            <SelectItem key={key}>
              {SECTION_LABELS[key]}
            </SelectItem>
          ))}
        </Select>
      </CardHeader>
    </Card>
  );
};
